/**
 * Recent PRIVA Runs — admin governance page.
 *
 * Read-only table of the latest `priva_ai_runs` rows across every
 * PrimeS4 AI application. Today populated exclusively by SmartEWM Ask
 * PRIVA; new applications will land here automatically as they start
 * writing audit rows.
 *
 * Out of scope for this iteration (placeholders only):
 *   - Charts / analytics rollups (Prompt Analytics, AI Cost Analytics).
 *   - Replay drawer / raw signal inspection.
 *   - Per-tenant governance dashboards.
 */
import { Fragment, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Loader2, RefreshCw, ShieldCheck, ChevronDown, ChevronRight } from "lucide-react";

// Dedicated admin API client. Cookies carry the admin JWT, matching
// every other admin page in this app.
const apiClient = axios.create({
  baseURL: process.env.REACT_APP_BACKEND_URL,
  withCredentials: true,
});

// Sentinel used by shadcn `<Select>` for the "Any" option — the
// component refuses an empty-string value, so we map it on the way in
// and out.
const ANY_VALUE = "__any__";

const RISK_OPTIONS = ["", "Low", "Medium", "High"];
const STATUS_OPTIONS = ["", "success", "fallback", "generic", "error"];
const TOPIC_OPTIONS = [
  "",
  "replenishment",
  "queue_attention",
  "supervisor_priority",
];
const FALLBACK_OPTIONS = [
  { value: "", label: "Any" },
  { value: "false", label: "Claude success" },
  { value: "true", label: "Fallback used" },
];

const RISK_BADGE_CLASS = {
  High: "bg-red-100 text-red-700 border border-red-200",
  Medium: "bg-amber-100 text-amber-700 border border-amber-200",
  Low: "bg-emerald-100 text-emerald-700 border border-emerald-200",
};

const STATUS_BADGE_CLASS = {
  success: "bg-emerald-100 text-emerald-700 border border-emerald-200",
  fallback: "bg-amber-100 text-amber-700 border border-amber-200",
  generic: "bg-gray-100 text-gray-700 border border-gray-200",
  error: "bg-red-100 text-red-700 border border-red-200",
};

const emptyFilters = {
  application: "",
  clientId: "",
  detectedTopic: "",
  riskLevel: "",
  fallbackUsed: "",
  responseStatus: "",
};

function formatTimestamp(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function formatTokens(usage) {
  if (!usage || usage.totalTokens == null) return "—";
  const inp = usage.inputTokens ?? "?";
  const out = usage.outputTokens ?? "?";
  return `${usage.totalTokens} (${inp} ⇢ ${out})`;
}

export default function RecentPrivaRunsPage() {
  const [filters, setFilters] = useState(emptyFilters);
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [refreshTick, setRefreshTick] = useState(0);
  const [expanded, setExpanded] = useState(null);

  const queryString = useMemo(() => {
    const p = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => {
      if (v) p.set(k, v);
    });
    p.set("limit", "50");
    return p.toString();
  }, [filters]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    apiClient
      .get(`/api/admin/priva/runs?${queryString}`)
      .then((res) => {
        if (cancelled) return;
        setRuns(res.data?.runs || []);
      })
      .catch((e) => {
        if (cancelled) return;
        const msg =
          e?.response?.data?.detail ||
          e?.message ||
          "Failed to load PRIVA runs.";
        setError(typeof msg === "string" ? msg : "Failed to load PRIVA runs.");
        setRuns([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [queryString, refreshTick]);

  const updateFilter = (key) => (value) =>
    setFilters((prev) => ({ ...prev, [key]: value }));

  return (
    <div className="p-6 max-w-[1480px] mx-auto" data-testid="priva-runs-page">
      <header className="flex items-start justify-between gap-6 mb-6">
        <div>
          <div className="flex items-center gap-2 mb-1.5">
            <ShieldCheck className="w-4 h-4 text-[#025979]" />
            <p className="text-[10px] uppercase tracking-widest text-[#025979] font-semibold">
              PRIVA Governance
            </p>
          </div>
          <h1
            className="text-xl font-bold text-[#111827]"
            style={{ fontFamily: "Chivo, sans-serif" }}
          >
            Recent PRIVA Runs
          </h1>
          <p className="text-xs text-[#6B7280] mt-1 max-w-2xl">
            The latest 50 AI operational runs across all PrimeS4 AI
            applications. Use filters to narrow by application, tenant,
            topic, risk, fallback path, or status.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setRefreshTick((t) => t + 1)}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-[#025979] border border-[#025979] rounded-sm hover:bg-[#025979]/5 transition-colors"
          data-testid="priva-runs-refresh"
        >
          <RefreshCw className="w-3.5 h-3.5" />
          Refresh
        </button>
      </header>

      {/* Filters */}
      <div
        className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-4"
        data-testid="priva-runs-filters"
      >
        <FilterInput
          testId="filter-application"
          label="Application"
          value={filters.application}
          onChange={updateFilter("application")}
          placeholder="e.g. SmartEWM"
        />
        <FilterInput
          testId="filter-clientId"
          label="Client"
          value={filters.clientId}
          onChange={updateFilter("clientId")}
          placeholder="clientId"
        />
        <FilterSelect
          testId="filter-topic"
          label="Topic"
          value={filters.detectedTopic}
          onChange={updateFilter("detectedTopic")}
          options={TOPIC_OPTIONS.map((t) => ({ value: t, label: t || "Any" }))}
        />
        <FilterSelect
          testId="filter-risk"
          label="Risk"
          value={filters.riskLevel}
          onChange={updateFilter("riskLevel")}
          options={RISK_OPTIONS.map((r) => ({ value: r, label: r || "Any" }))}
        />
        <FilterSelect
          testId="filter-fallback"
          label="Fallback"
          value={filters.fallbackUsed}
          onChange={updateFilter("fallbackUsed")}
          options={FALLBACK_OPTIONS}
        />
        <FilterSelect
          testId="filter-status"
          label="Status"
          value={filters.responseStatus}
          onChange={updateFilter("responseStatus")}
          options={STATUS_OPTIONS.map((s) => ({ value: s, label: s || "Any" }))}
        />
      </div>

      {/* Table */}
      <div className="bg-white border border-[#E5E7EB] rounded-sm shadow-sm overflow-x-auto">
        <table className="min-w-full text-xs" data-testid="priva-runs-table">
          <thead className="bg-[#F8FAFC] text-[10px] uppercase tracking-wider text-[#6B7280]">
            <tr>
              <Th>Timestamp</Th>
              <Th>Application</Th>
              {/* Hidden governance placeholders — kept in the DOM so
                  downstream tests/screenshots can verify the schema
                  is wired end-to-end. Toggle the `hidden` class off
                  once the governance UI is ready to expose them. */}
              <Th className="hidden" data-testid="priva-runs-th-module">Module</Th>
              <Th className="hidden" data-testid="priva-runs-th-experience">Experience</Th>
              <Th className="hidden" data-testid="priva-runs-th-channel">Channel</Th>
              {/* TODO(taxonomy): expose these once the hierarchical
                  operational intelligence taxonomy lands. */}
              <Th className="hidden" data-testid="priva-runs-th-domain">Domain</Th>
              <Th className="hidden" data-testid="priva-runs-th-subtopic">SubTopic</Th>
              <Th>Client</Th>
              <Th>User</Th>
              <Th>Role</Th>
              <Th>Topic</Th>
              <Th>Risk</Th>
              <Th>Model</Th>
              <Th>Fallback</Th>
              <Th>Latency</Th>
              <Th>Tokens</Th>
              <Th>Status</Th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#E5E7EB]">
            {loading ? (
              <tr>
                <td colSpan={17} className="text-center py-12 text-[#6B7280]">
                  <Loader2 className="w-4 h-4 mx-auto animate-spin" />
                </td>
              </tr>
            ) : error ? (
              <tr>
                <td
                  colSpan={17}
                  className="text-center py-10 text-red-600 text-xs"
                  data-testid="priva-runs-error"
                >
                  {error}
                </td>
              </tr>
            ) : runs.length === 0 ? (
              <tr>
                <td
                  colSpan={17}
                  className="text-center py-10 text-[#6B7280]"
                  data-testid="priva-runs-empty"
                >
                  No PRIVA runs match the current filters.
                </td>
              </tr>
            ) : (
              runs.map((run) => (
                <Fragment key={run.runId}>
                <tr
                  className="hover:bg-[#F8FAFC]/70 cursor-pointer"
                  data-testid={`priva-run-${run.runId}`}
                  onClick={() => setExpanded((id) => (id === run.runId ? null : run.runId))}
                >
                  <Td className="whitespace-nowrap font-mono text-[11px] text-[#374151]">
                    <span className="inline-flex items-center gap-1">
                      {expanded === run.runId
                        ? <ChevronDown className="w-3 h-3 text-[#025979]" />
                        : <ChevronRight className="w-3 h-3 text-[#9CA3AF]" />}
                      {formatTimestamp(run.timestamp)}
                    </span>
                  </Td>
                  <Td>
                    {/*
                      PRIVA Governance — Feb 2026.
                      Prefer the new top-level `application` field;
                      gracefully fall back to legacy `applicationAccess`
                      for rows written before the governance taxonomy
                      landed.
                    */}
                    <Badge
                      className="bg-[#E0F2FE] text-[#025979] border-[#025979]/20 font-medium"
                      data-testid={`priva-run-${run.runId}-application`}
                    >
                      {run.application || run.applicationAccess || "—"}
                    </Badge>
                  </Td>
                  {/* Hidden governance placeholders — see the matching
                      <Th> block above. Data is present in the DOM so
                      tests can assert wiring without exposing the
                      columns visually yet. */}
                  <Td
                    className="hidden"
                    data-testid={`priva-run-${run.runId}-module`}
                  >
                    {run.module || "—"}
                  </Td>
                  <Td
                    className="hidden"
                    data-testid={`priva-run-${run.runId}-experience`}
                  >
                    {run.experience || "—"}
                  </Td>
                  <Td
                    className="hidden"
                    data-testid={`priva-run-${run.runId}-channel`}
                  >
                    {run.channel || "—"}
                  </Td>
                  <Td
                    className="hidden"
                    data-testid={`priva-run-${run.runId}-domain`}
                  >
                    {run.domain || "—"}
                  </Td>
                  <Td
                    className="hidden"
                    data-testid={`priva-run-${run.runId}-subtopic`}
                  >
                    {run.subTopic || "—"}
                  </Td>
                  <Td>
                    <p className="font-medium text-[#111827]">
                      {run.clientName || run.clientId || "—"}
                    </p>
                    {run.clientName && run.clientId && (
                      <p className="text-[10px] text-[#9CA3AF] font-mono">
                        {run.clientId}
                      </p>
                    )}
                  </Td>
                  <Td className="text-[#374151]">{run.userEmail || "—"}</Td>
                  <Td className="text-[#374151]">
                    {run.applicationRole || "—"}
                  </Td>
                  <Td>
                    {run.detectedTopic ? (
                      <span className="text-[#374151] font-medium">
                        {run.detectedTopic}
                      </span>
                    ) : (
                      <span className="text-[#9CA3AF]">—</span>
                    )}
                    {run.isFollowup ? (
                      <p className="text-[10px] text-[#7C3AED] mt-0.5">
                        follow-up
                      </p>
                    ) : null}
                  </Td>
                  <Td>
                    {run.riskLevel ? (
                      <Badge
                        className={
                          RISK_BADGE_CLASS[run.riskLevel] ||
                          "bg-gray-100 text-gray-700 border border-gray-200"
                        }
                      >
                        {run.riskLevel}
                      </Badge>
                    ) : (
                      <span className="text-[#9CA3AF]">—</span>
                    )}
                  </Td>
                  <Td className="text-[#374151] whitespace-nowrap">
                    {run.modelUsed || "—"}
                    {run.promptVersion && (
                      <p className="text-[10px] text-[#9CA3AF]">
                        prompt {run.promptVersion}
                      </p>
                    )}
                  </Td>
                  <Td>
                    {run.fallbackUsed ? (
                      <Badge className="bg-amber-100 text-amber-700 border border-amber-200">
                        Yes
                      </Badge>
                    ) : (
                      <span className="text-[#6B7280]">No</span>
                    )}
                  </Td>
                  <Td className="font-mono text-[11px] text-[#374151]">
                    {run.latencyMs != null ? `${run.latencyMs} ms` : "—"}
                  </Td>
                  <Td className="font-mono text-[11px] text-[#374151] whitespace-nowrap">
                    {formatTokens(run.tokenUsage)}
                  </Td>
                  <Td>
                    <Badge
                      className={
                        STATUS_BADGE_CLASS[run.responseStatus] ||
                        "bg-gray-100 text-gray-700 border border-gray-200"
                      }
                    >
                      {run.responseStatus || "—"}
                    </Badge>
                  </Td>
                </tr>
                {expanded === run.runId && (
                  <tr data-testid={`priva-run-${run.runId}-details`}>
                    <td colSpan={17} className="bg-[#F8FAFC] px-4 py-4 border-t border-[#E5E7EB]">
                      <RunDetail run={run} />
                    </td>
                  </tr>
                )}
                </Fragment>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function RunDetail({ run }) {
  const extra = run.extra || {};
  const signals = Array.isArray(extra.signalsUsed) ? extra.signalsUsed : [];
  const ai = extra.agentIntelligence || {};
  const domain = ai.domain || run.domain || "—";
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-3 text-xs" data-testid={`priva-run-detail-${run.runId}`}>
      <DetailItem label="User Question" wide value={run.question} testId="detail-question" />
      <DetailItem label="Priority Summary" wide value={extra.prioritySummary} testId="detail-priority" />
      <DetailItem label="Recommendation Generated" wide value={run.recommendedAction} testId="detail-recommendation" />
      <DetailItem label="Application" value={run.application || run.applicationAccess} />
      <DetailItem label="Business Client" value={run.clientName || run.clientId} />
      <DetailItem label="SAP System" value={extra.sapSystem} testId="detail-sap-system" />
      <DetailItem label="Warehouse" value={run.warehouse} testId="detail-warehouse" />
      <DetailItem label="Domain Detected" value={domain} testId="detail-domain" />
      <DetailItem label="Risk Level" value={run.riskLevel} />
      <div data-testid="detail-signals-used">
        <p className="text-[10px] uppercase tracking-wider text-[#94A3B8] font-semibold mb-1">Signals Used</p>
        {signals.length ? (
          <div className="flex flex-wrap gap-1.5">
            {signals.map((s) => (
              <Badge key={s} className="bg-[#E0F2FE] text-[#025979] border border-[#025979]/20">{s}</Badge>
            ))}
          </div>
        ) : <span className="text-[#9CA3AF]">—</span>}
      </div>
      <DetailItem label="Response Status" value={run.responseStatus} />
      <DetailItem label="Timestamp" value={formatTimestamp(run.timestamp)} />
      <DetailItem label="Latency" value={run.latencyMs != null ? `${run.latencyMs} ms` : null} />
      <DetailItem label="Tokens" value={formatTokens(run.tokenUsage)} />
    </div>
  );
}

function DetailItem({ label, value, wide, testId }) {
  return (
    <div className={wide ? "sm:col-span-2 lg:col-span-3" : ""} data-testid={testId}>
      <p className="text-[10px] uppercase tracking-wider text-[#94A3B8] font-semibold mb-0.5">{label}</p>
      <p className="text-[#1F2937] break-words whitespace-pre-wrap">{value || "—"}</p>
    </div>
  );
}

function FilterInput({ label, value, onChange, placeholder, testId }) {
  return (
    <label className="flex flex-col gap-1 text-[10px] uppercase tracking-wider text-[#6B7280]">
      {label}
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="h-8 text-xs"
        data-testid={testId}
      />
    </label>
  );
}

function FilterSelect({ label, value, onChange, options, testId }) {
  return (
    <label className="flex flex-col gap-1 text-[10px] uppercase tracking-wider text-[#6B7280]">
      {label}
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="h-8 text-xs" data-testid={testId}>
          <SelectValue placeholder="Any" />
        </SelectTrigger>
        <SelectContent>
          {options.map((opt) => (
            <SelectItem
              key={opt.value || "__any__"}
              value={opt.value || "__any__"}
              data-testid={`${testId}-option-${opt.value || "any"}`}
              onSelect={() => {}}
            >
              {opt.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </label>
  );
}

function Th({ children, className = "", ...rest }) {
  return (
    <th
      className={`text-left font-medium px-3 py-2 whitespace-nowrap ${className}`}
      {...rest}
    >
      {children}
    </th>
  );
}

function Td({ children, className = "", ...rest }) {
  return (
    <td className={`px-3 py-2 align-top ${className}`} {...rest}>
      {children}
    </td>
  );
}
